import type {
  CustomerItemAnswer,
  PastRound,
  ReviewVerdict,
  StylingReviewDetail,
  StylingReviewItem,
} from '@cortex/domain-ac-mens-hub-api';
import { Link, useParams } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveStylingReviewMark, useStylingReviewDetail } from '../hooks/useStylingReview.js';

/**
 * 選定1件の詳細。
 *
 * 「どんな人に → どんな要望に → 何を送ったか → 自分の見立て → お客様の答え」を
 * 上から順に読めるよう、この順序で並べる。判断材料が4つのデータ源に散っているため、
 * 1画面に集約すること自体がこの画面の目的。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定1件の判断材料を1画面に集約し、見立てを残せるようにする。
 * @graph-connects useStylingReviewDetail [calls] 詳細の取得
 * @graph-connects useSaveStylingReviewMark [calls] 見立ての保存
 */

/**
 * 実寸を「肩幅 45.5cm ／ 身幅 54cm」の形にまとめる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 服の実寸を1行で読める形にまとめる。
 * @graph-connects none
 */
export function formatSpecifications(item: StylingReviewItem): string {
  return item.specifications.map((s) => `${s.name} ${String(s.value)}${s.unit}`).join(' ／ ');
}

/**
 * サイズ感の翻訳キー。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 着ごとのサイズ感を画面の言葉に対応づける。
 * @graph-connects none
 */
export function sizeFeelingLabelKey(answer: CustomerItemAnswer): string | null {
  if (answer.sizeFeeling === null) return null;
  return `stylingReview.sizeFeeling.${answer.sizeFeeling}`;
}

/**
 * カルテの表示行（見出しの翻訳キーと値）を組み立てる。
 * 未登録の項目は値を `null` にして呼び出し側で落とす（空欄の行を並べない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business カルテの表示行を組み立てる。未登録の項目は行ごと出さない。
 * @graph-connects none
 */
export function karteRows(detail: StylingReviewDetail): [string, string | null][] {
  return [
    ['stylingReview.occupation', detail.customer.occupation],
    ['stylingReview.industry', detail.customer.industry],
    ['stylingReview.officeFashion', detail.customer.officeFashion],
    ['stylingReview.groupTaste', detail.customer.groupTaste],
    [
      'stylingReview.firstImpression',
      detail.customer.firstImpressions.length === 0
        ? null
        : detail.customer.firstImpressions.join('・'),
    ],
  ];
}

/** セクション見出し。 @graph-connects none */
function Step({
  index,
  title,
  right,
  children,
}: {
  index: number;
  title: string;
  right?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-accent-primary)] text-[14px] font-semibold text-white">
          {index}
        </span>
        <h2 className="text-[17px] font-semibold text-[var(--color-text-primary)]">{title}</h2>
        {right === undefined ? null : (
          <span className="ml-auto text-[14px] text-[var(--color-text-muted)]">{right}</span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

/** 数値の見出し付きボックス。 @graph-connects none */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-[110px] rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3">
      <div className="text-[13px] text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-0.5 text-[26px] font-semibold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </div>
    </div>
  );
}

/** 過去1回分。 @graph-connects none */
function PastRoundBlock({ round, t }: { round: PastRound; t: (key: string) => string }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
      <div className="flex flex-wrap items-center gap-3 text-[14px]">
        <b className="text-[15px] text-[var(--color-text-primary)]">
          {round.orderSeq}
          {t('stylingReview.roundSuffix')}
        </b>
        <span className="text-[var(--color-text-muted)]">
          {round.completedAt === null ? '' : round.completedAt.slice(0, 10)}
        </span>
        {round.customerVerdict === null ? (
          <span className="text-[var(--color-text-muted)]">{t('stylingReview.waitingAnswer')}</span>
        ) : (
          <span
            className={
              round.customerVerdict === 'good'
                ? 'text-[var(--color-success)]'
                : 'text-[var(--color-error)]'
            }
          >
            {round.customerVerdict === 'good'
              ? t('stylingReview.answer.good')
              : t('stylingReview.answer.bad')}
          </span>
        )}
        <span className="text-[var(--color-text-secondary)]">
          {t('stylingReview.misfitCount')} {round.misfitCount}/{round.itemCount} ・{' '}
          {t('stylingReview.usedCount')} {round.usedCount}/{round.itemCount}
        </span>
      </div>
      {round.comments.map((comment, index) => (
        <p
          // 同じ回の別アイテムに同一の感想（「特になし」等）が入りうるため、本文を key にしない
          key={`${String(round.phoenixOrderId)}-${String(index)}`}
          className="mt-2 text-[15px] leading-relaxed text-[var(--color-text-secondary)]"
        >
          {comment}
        </p>
      ))}
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定1件を上から読んで判断し、見立てを残す画面。
 * @graph-connects useStylingReviewDetail [calls] 詳細の取得
 * @graph-connects useSaveStylingReviewMark [calls] 見立ての保存
 */
export function StylingReviewDetailPage() {
  const { t } = useTranslation();
  const { phoenixOrderId } = useParams({ from: '/styling-review/$phoenixOrderId' });
  const orderId = Number(phoenixOrderId);
  const { data, isLoading, error } = useStylingReviewDetail(orderId);
  const save = useSaveStylingReviewMark();
  const [verdict, setVerdict] = useState<ReviewVerdict | null>(null);
  const [memo, setMemo] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error || data === undefined) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  const currentVerdict = verdict ?? data.myVerdict;
  const currentMemo = memo ?? data.myMemo ?? '';
  const answersBySku = new Map(data.customerItemAnswers.map((a) => [a.phoenixItemSkuId, a]));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/styling-review"
          className="text-[14px] text-[var(--color-accent-primary)] hover:underline"
        >
          ← {t('stylingReview.backToList')}
        </Link>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {data.completedAt.slice(0, 16).replace('T', ' ')}
          {data.stylistName === null ? '' : ` ／ ${data.stylistName}`}
        </h1>
      </div>

      <Step
        index={1}
        title={t('stylingReview.step.customer')}
        right={t('stylingReview.photoCount', { count: data.customer.photoUrls.length })}
      >
        {data.customer.photoUrls.length === 0 ? (
          <p className="text-[15px] text-[var(--color-text-muted)]">{t('stylingReview.noPhoto')}</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {data.customer.photoUrls.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                loading="lazy"
                className="h-[280px] w-[210px] shrink-0 rounded-md border border-[var(--color-border)] object-cover"
              />
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-[22px] font-semibold text-[var(--color-text-primary)]">
            {data.customer.name ?? String(data.customer.arthurUserId)}
          </span>
          {data.customer.nameKana === null ? null : (
            <span className="text-[14px] text-[var(--color-text-muted)]">
              {data.customer.nameKana}
            </span>
          )}
          {data.customer.age === null ? null : (
            <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-[14px] text-[var(--color-text-secondary)]">
              {t('stylingReview.age', { count: data.customer.age })}
            </span>
          )}
          <span className="font-mono text-[13px] text-[var(--color-text-muted)]">
            {data.customer.arthurUserId}
          </span>
          <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-[14px] text-[var(--color-text-secondary)]">
            {data.orderSeq === 1
              ? t('stylingReview.firstOrder')
              : t('stylingReview.nthOrder', { count: data.orderSeq })}
          </span>
          {data.customer.planName === null ? null : (
            <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-[14px] text-[var(--color-text-secondary)]">
              {data.customer.planName}
              {data.customer.planPrice === null
                ? ''
                : ` ¥${data.customer.planPrice.toLocaleString('ja-JP')}`}
            </span>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[15px]">
          {karteRows(data)
            .filter((entry): entry is [string, string] => entry[1] !== null)
            .map(([labelKey, value]) => (
              <Fragment key={labelKey}>
                <dt className="text-[var(--color-text-muted)]">{t(labelKey)}</dt>
                <dd className="m-0 text-[var(--color-text-primary)]">{value}</dd>
              </Fragment>
            ))}
        </dl>
        <div className="mt-4 flex flex-wrap gap-3">
          <Fact
            label={t('stylingReview.height')}
            value={data.customer.height === null ? '—' : String(data.customer.height)}
          />
          <Fact
            label={t('stylingReview.weight')}
            value={data.customer.weight === null ? '—' : String(data.customer.weight)}
          />
          <Fact label={t('stylingReview.topsSize')} value={data.customer.topsSize ?? '—'} />
          <Fact label={t('stylingReview.pantsSize')} value={data.customer.pantsSize ?? '—'} />
        </div>
      </Step>

      <Step index={2} title={t('stylingReview.step.request')}>
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
          <div className="text-[13px] text-[var(--color-text-muted)]">
            {t('stylingReview.requestText')}
          </div>
          <p className="mt-1 text-[17px] leading-relaxed text-[var(--color-text-primary)]">
            {data.request.text ?? t('stylingReview.noRequest')}
          </p>
        </div>
        {data.history.length === 0 ? (
          <p className="mt-3 text-[15px] text-[var(--color-text-muted)]">
            {t('stylingReview.noHistory')}
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {data.history.map((round) => (
              <PastRoundBlock key={round.phoenixOrderId} round={round} t={t} />
            ))}
          </div>
        )}
      </Step>

      <Step
        index={3}
        title={t('stylingReview.step.items')}
        right={
          data.totalRetailPrice === null
            ? undefined
            : `${t('stylingReview.totalRetailPrice')} ¥${data.totalRetailPrice.toLocaleString('ja-JP')}`
        }
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          {data.items.map((item) => (
            <div
              key={item.phoenixItemSkuId}
              className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
            >
              {item.imageUrl === null ? (
                <div className="flex aspect-[3/4] items-center justify-center bg-[var(--color-bg-base)] text-[13px] text-[var(--color-text-muted)]">
                  {t('stylingReview.noItemImage')}
                </div>
              ) : (
                <img
                  src={item.imageUrl}
                  alt=""
                  loading="lazy"
                  className="aspect-[3/4] w-full bg-[var(--color-bg-base)] object-contain"
                />
              )}
              <div className="p-3">
                <div className="text-[12px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  {item.brandName ?? '—'}
                </div>
                <div className="mt-0.5 text-[15px] font-medium text-[var(--color-text-primary)]">
                  {item.itemName ?? '—'}
                </div>
                <div className="mt-2 flex items-baseline justify-between gap-2">
                  <span className="text-[13px] text-[var(--color-text-secondary)]">
                    {item.categoryName ?? '—'}
                    {item.acSize === null ? '' : ` ・ ${item.acSize}`}
                  </span>
                  <span className="text-[18px] font-semibold tabular-nums text-[var(--color-text-primary)]">
                    {item.retailPrice === null
                      ? '—'
                      : `¥${item.retailPrice.toLocaleString('ja-JP')}`}
                  </span>
                </div>
                {item.specifications.length === 0 ? null : (
                  <div className="mt-2 border-t border-[var(--color-border)] pt-2 text-[13px] tabular-nums text-[var(--color-text-secondary)]">
                    {formatSpecifications(item)}
                  </div>
                )}
                {item.isUserPicked ? (
                  <div className="mt-2 text-[13px] text-[var(--color-accent-primary)]">
                    {t('stylingReview.userPicked')}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        {data.stylistComment === null ? null : (
          <div className="mt-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
            <div className="text-[13px] text-[var(--color-text-muted)]">
              {t('stylingReview.stylistComment')}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
              {data.stylistComment}
            </p>
          </div>
        )}
      </Step>

      <Step index={4} title={t('stylingReview.step.myVerdict')}>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            aria-pressed={currentVerdict === 'good'}
            onClick={() => {
              setVerdict('good');
            }}
            className={`min-w-[180px] flex-1 rounded-lg border-2 px-6 py-5 text-[20px] font-semibold transition-colors ${
              currentVerdict === 'good'
                ? 'border-[var(--color-success)] bg-[var(--color-success-solid)] text-[var(--color-success)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
            }`}
          >
            {t('stylingReview.verdict.good')}
          </button>
          <button
            type="button"
            aria-pressed={currentVerdict === 'bad'}
            onClick={() => {
              setVerdict('bad');
            }}
            className={`min-w-[180px] flex-1 rounded-lg border-2 px-6 py-5 text-[20px] font-semibold transition-colors ${
              currentVerdict === 'bad'
                ? 'border-[var(--color-error)] bg-[var(--color-error-solid)] text-[var(--color-error)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
            }`}
          >
            {t('stylingReview.verdict.bad')}
          </button>
        </div>
        <textarea
          value={currentMemo}
          onChange={(e) => {
            setMemo(e.target.value);
          }}
          placeholder={t('stylingReview.memoPlaceholder')}
          className="mt-3 min-h-[96px] w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 text-[15px] text-[var(--color-text-primary)]"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => {
              save.mutate({
                phoenixOrderId: orderId,
                verdict: currentVerdict,
                memo: currentMemo === '' ? null : currentMemo,
              });
            }}
            className="rounded-md bg-[var(--color-accent-primary)] px-6 py-3 text-[16px] font-medium text-white disabled:opacity-50"
          >
            {t('stylingReview.save')}
          </button>
          {save.isSuccess ? (
            <span className="text-[14px] text-[var(--color-success)]">
              {t('stylingReview.saved')}
            </span>
          ) : null}
          {save.isError ? (
            <span className="text-[14px] text-[var(--color-error)]">
              {t('stylingReview.saveError')}
            </span>
          ) : null}
        </div>
      </Step>

      <Step index={5} title={t('stylingReview.step.answer')}>
        {data.customerVerdict === null ? (
          <p className="text-[15px] text-[var(--color-text-muted)]">
            {t('stylingReview.waitingAnswerDescription')}
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span
                className={`rounded-full border px-4 py-1.5 text-[16px] font-medium ${
                  data.customerVerdict === 'good'
                    ? 'border-[var(--color-success)] bg-[var(--color-success-solid)] text-[var(--color-success)]'
                    : 'border-[var(--color-error)] bg-[var(--color-error-solid)] text-[var(--color-error)]'
                }`}
              >
                {data.customerVerdict === 'good'
                  ? t('stylingReview.answer.good')
                  : t('stylingReview.answer.bad')}
              </span>
              {data.myVerdict === null ? null : (
                <span className="text-[14px] text-[var(--color-text-muted)]">
                  {data.myVerdict === data.customerVerdict
                    ? t('stylingReview.matched')
                    : t('stylingReview.mismatched')}
                </span>
              )}
            </div>
            <div className="overflow-hidden rounded-md border border-[var(--color-border)]">
              {data.items.map((item) => {
                const answer = answersBySku.get(item.phoenixItemSkuId);
                const feelingKey = answer === undefined ? null : sizeFeelingLabelKey(answer);
                return (
                  <div
                    key={item.phoenixItemSkuId}
                    className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 last:border-b-0 text-[14px]"
                  >
                    <span className="min-w-[160px] flex-1 text-[var(--color-text-primary)]">
                      {item.itemName ?? '—'}
                    </span>
                    {feelingKey === null ? null : (
                      <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--color-text-secondary)]">
                        {t(feelingKey)}
                      </span>
                    )}
                    {answer?.isUsed === undefined || answer.isUsed === null ? null : (
                      <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--color-text-secondary)]">
                        {answer.isUsed ? t('stylingReview.used') : t('stylingReview.notUsed')}
                      </span>
                    )}
                    {answer?.colorRating == null ? null : (
                      <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--color-text-secondary)]">
                        {t('stylingReview.colorRating')} {answer.colorRating}
                      </span>
                    )}
                    {answer?.designRating == null ? null : (
                      <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--color-text-secondary)]">
                        {t('stylingReview.designRating')} {answer.designRating}
                      </span>
                    )}
                    {answer?.comment == null ? null : (
                      <span className="w-full text-[14px] text-[var(--color-text-secondary)]">
                        {answer.comment}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Step>
    </div>
  );
}
